import Boards from '../../services/Boards';
import { createRestSlices, applyReducers } from './rest';
import { ActionPayload } from '..';
import { Board, Change, Quote } from '../../models';
import type { ColumnData } from '../../models';
import { entityId, mergePopulatedChangeRefs } from './remoteUpdate';

const {
    initialState,
    createReducer, startCreateReducer,
    getReducer, listReducer,
    updateReducer, deleteReducer,
    duplicateReducer,
    createAction, startCreateAction,
    getAction, listAction,
    updateAction, deleteAction,
    duplicateAction,
} = createRestSlices(Boards);

/**
 * Attach / detach a quote on embedded board cards so badges survive modal close.
 * Budget edits often go through quote CRUD, which never emits UPDATING_CHANGE_SUCCESS.
 */
const patchBoardWithQuote = (board: Board, quote: AnyObj): Board => {
    const quoteId = entityId(quote);
    const linkedIds = new Set(
        (quote.items || [])
            .map((item: AnyObj) => entityId(item.change))
            .filter(Boolean)
    );

    const columns = board.columns.map((column: ColumnData) => ({
        ...column,
        changes: column.changes.map((original: Change) => {
            if (linkedIds.has(original._id))
                return new Change({ ...original, quote: new Quote(quote) });
            if (entityId(original.quote) === quoteId)
                return new Change({ ...original, quote: undefined });
            return original;
        }),
    }));

    return new Board({
        ...board,
        columns,
        updatedAt: new Date().toISOString(),
    });
};

const replaceBoardChange = (board: Board, change: Change): Board => {
    const shouldDrop = (
        change.progress?.status === Change.STATUS_DISCARDED
        || (change.type === Change.TYPE_NONE && change.progress?.status === Change.STATUS_FINISHED)
    );

    if (shouldDrop) {
        return new Board({
            ...board,
            columns: board.columns.map((column: ColumnData) => ({
                ...column,
                changes: column.changes.filter((original: Change) => original._id !== change._id),
            })),
            updatedAt: new Date().toISOString(),
        });
    }

    return new Board({
        ...board,
        columns: board.columns.map((column: ColumnData) => ({
            ...column,
            changes: column.changes.map((original: Change) => (
                original._id === change._id
                    ? new Change(mergePopulatedChangeRefs(change, original))
                    : original
            )),
        })),
        // Bump updatedAt so Column's forDisplay memo recomputes after
        // embedded change patches (labels, assignee, quote, …). Without it,
        // Card falls back to stale board props when the modal closes.
        updatedAt: new Date().toISOString(),
    });
};

/**
 * We have to add this hook to handle remote modifications on changes
 */
const UPDATE = 'yoda/mercure/REMOTE_UPDATE';
const onRemoteUpdateReducer = (state = initialState, action: ActionPayload) => {
    if (action.type !== UPDATE)
        return state;

    const { update: { data, type }} = action;
    switch (type) {
        case 'board.created':
            if (!state.boards)
                return {
                    ...state,
                    boards: [new Board(data)]
                };
            if (state.boards.some((board: Board) => board._id === data._id))
                return state;
            return {
                ...state,
                boards: [new Board(data), ...state.boards]
            };
        case 'board.updated':
            if (!state.board || state.board._id !== data._id)
                return state;

            return {
                ...state,
                board: new Board({
                    ...state.board,
                    ...data,
                })
            };
        case 'change.updated':
            if (!state.board)
                return state;

            return {
                ...state,
                board: replaceBoardChange(state.board, new Change(data)),
            };
        case 'change.deleted':
        case 'change.merged':
            if (!state.board)
                return state;
            const removedIds = type === 'change.deleted'
                ? [data._id]
                : (data.sources || []).map((source: Change) => source._id);
            return {
                ...state,
                board: new Board({
                    ...state.board,
                    columns: state.board.columns.map((column: ColumnData) => ({
                        ...column,
                        changes: column.changes.filter((original: Change) => !removedIds.includes(original._id))
                    })),
                    updatedAt: new Date().toISOString(),
                })
            };
        case 'quote.created':
        case 'quote.updated':
            if (!state.board)
                return state;
            return {
                ...state,
                board: patchBoardWithQuote(state.board, data),
            };
        default:
            return state;
    }
};

/**
 * We have to add this hook to handle modifications on changes
 */
const TO_STEP_SUCCESS         = 'yoda/change/TO_STEP_SUCCESS';
const UPDATING_CHANGE_SUCCESS = 'yoda/change/UPDATING_ITEM_SUCCESS';
const ADD_QUOTE_SUCCESS       = 'yoda/change/ADD_QUOTE_SUCCESS';
const UPDATING_QUOTE_SUCCESS  = 'yoda/quote/UPDATING_ITEM_SUCCESS';
const TO_QUOTE_STEP_SUCCESS   = 'yoda/quote/TO_STEP_SUCCESS';
const updateChangeReducer = (state = initialState, action: ActionPayload) => {
    if (!state.board)
        return state;

    switch (action.type) {
        case TO_STEP_SUCCESS:
        case UPDATING_CHANGE_SUCCESS:
        case ADD_QUOTE_SUCCESS:
            return {
                ...state,
                board: replaceBoardChange(state.board, action.change),
            };
        case TO_QUOTE_STEP_SUCCESS:
        case UPDATING_QUOTE_SUCCESS:
            return {
                ...state,
                board: patchBoardWithQuote(state.board, action.quote),
            };
        default:
            return state;
    }
};

/* Export reducer */
/* eslint import/no-anonymous-default-export: [2, {"allowArrowFunction": true}] */
export default (state = initialState, action: ActionPayload) => {
    return applyReducers(state, action, [
        createReducer, startCreateReducer,
        getReducer, listReducer,
        updateReducer, deleteReducer,
        duplicateReducer,
        updateChangeReducer, onRemoteUpdateReducer,
    ]);
}

/**
 * Reload the current board when it has smart columns.
 * Own Mercure echoes are ignored (subscriber id), so local step/update
 * must refresh membership explicitly.
 */
export const reloadBoardIfSmart = () => {
    return (dispatch: any, getState: () => AnyObj) => {
        const board = getState()?.boards?.board as Board | undefined;
        if (!board?._id)
            return;

        const hasSmart = (board.columns || []).some((column: ColumnData) => column.isSmart);
        if (hasSmart)
            dispatch(getAction(board._id));
    };
};

/* Export CRUD actions */
export const createBoard        = createAction;
export const startCreateBoard   = startCreateAction;
export const loadBoard          = getAction;
export const loadBoards         = listAction;
export const updateBoard        = updateAction;
export const deleteBoard        = deleteAction;
export const duplicateBoard     = duplicateAction;
